<template>
    <div class="skill-tree-container">
        <!-- Archetype selection -->
        <div class="input-group mb-3 mt-3">
            <label class="input-group-text" for="abilitySelect">Ability Tree</label>
            <select
                class="form-select"
                id="abilitySelect"
                v-model="selectedAbilityList"
            >
                <option value="" disabled>
                    Please select an ability tree...
                </option>
                <option
                    v-for="archetype in archetypes"
                    :key="archetype.archetype"
                    :value="archetype.archetype"
                >
                    {{ archetype.archetype }}
                </option>
            </select>
        </div>

        <!-- Skill Tree Display -->
        <div v-if="paths.length" class="skill-tree-scroll" ref="scrollRef">
            <div
                class="skilltree"
                :style="{
                    gridTemplateColumns: `repeat(${paths.length}, 1fr)`,
                    gridTemplateRows: `repeat(${tiers.length}, 8rem)`
                }"
            >
                <!-- Loop through tiers -->
                <template v-for="tier in tiers" :key="'tier-' + tier">
                    <!-- Special-case rows: None tier 1 or All -->
                    <div
                        v-if="tier === 1 && abilityTree[selectedAbilityList]['None']?.[1]"
                        class="tier-cell full-width"
                    >
                        <SkillNode
                            v-for="skill in abilityTree[selectedAbilityList]['None'][1]"
                            :key="skill.id"
                            :skill="skill"
                            :state="getNodeState(skill)"
                            class="skill-node"
                            @skill-clicked="openSkillModal"
                        />
                    </div>

                    <div
                        v-else-if="abilityTree[selectedAbilityList]['All']?.[tier]"
                        class="tier-cell full-width"
                    >
                        <SkillNode
                            v-for="skill in abilityTree[selectedAbilityList]['All'][tier]"
                            :key="skill.id"
                            :skill="skill"
                            :state="getNodeState(skill)"
                            class="skill-node"
                            @skill-clicked="openSkillModal"
                        />
                    </div>

                    <!-- Regular path abilities -->
                    <div
                        v-else
                        v-for="path in paths"
                        :key="path + '-tier-' + tier"
                        class="tier-cell"
                    >
                        <SkillNode
                            v-for="skill in abilityTree[selectedAbilityList][path]?.[tier] || []"
                            :key="skill.id"
                            :skill="skill"
                            :state="getNodeState(skill)"
                            class="skill-node"
                            :class="{
                                split: (abilityTree[selectedAbilityList][path]?.[tier] || []).length > 1
                            }"
                            @skill-clicked="openSkillModal"
                        />
                    </div>
                </template>
            </div>
        </div>

        <!-- Modal -->
        <div
            class="modal fade"
            id="skillModal"
            tabindex="-1"
            aria-hidden="true"
            ref="modalRef"
        >
            <div class="modal-dialog modal-lg modal-dialog-centered">
                <div class="modal-content bg-dark text-light rounded-3 shadow-lg">
                    <div class="modal-header border-secondary">
                        <div class="d-flex align-items-center gap-3">
                            <h4 class="modal-title mb-0">
                                {{ selectedSkill?.name }}
                            </h4>
                        </div>
                        <button
                            type="button"
                            class="btn-close btn-close-white"
                            data-bs-dismiss="modal"
                        ></button>
                    </div>

                    <div class="modal-body">
                        <p class="fs-5">{{ selectedSkill?.description }}</p>
                        <hr class="border-secondary" />
                        <p>
                            <strong>Required Stat: </strong>
                            <span class="text-info">
                                {{
                                    selectedSkill?.requiredStat
                                        ?.split(" ")
                                        .map(
                                            (word) =>
                                                word.charAt(0).toUpperCase() +
                                                word.slice(1)
                                        )
                                        .join(" ")
                                }}
                            </span>
                        </p>

                        <p v-if="selectedSkill?.requiredAbility?.length">
                            <strong>Required Ability: </strong>
                            <span class="text-warning">
                                {{
                                    selectedSkill.requiredAbility
                                        .map(
                                            (id) =>
                                                abilities.find(
                                                    (a) => a.id === id
                                                )?.name
                                        )
                                        .join(", ")
                                }}
                            </span>
                        </p>
                        <p v-else>
                            <strong>Required Ability: </strong>
                            <span class="text-success">None</span>
                        </p>
                    </div>

                    <div class="modal-footer border-secondary">
                        <button
                            type="button"
                            class="btn btn-outline-light btn-lg px-4"
                            data-bs-dismiss="modal"
                        >
                            <font-awesome-icon icon="multiply" class="me-1" />
                            Close
                        </button>
                        <button
                            type="button"
                            class="btn btn-primary btn-lg px-4"
                            :disabled="selectedState !== 'available'"
                            @click="unlockAbility(selectedSkill)"
                        >
                            {{
                                selectedState === "available"
                                    ? "Learn Ability"
                                    : "Not Available"
                            }}
                        </button>

                        <button
                            type="button"
                            class="btn btn-danger btn-lg px-4"
                            v-if="selectedState === 'Active'"
                            @click="forgetAbility(selectedSkill)"
                        >
                            Unlearn Ability
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import SkillNode from "./SkillNode.vue";
import { useRosterStore } from "@/stores/index";

export default {
    name: "AbilityTree",
    components: { SkillNode },
    props: {
        initial: { type: Object, default: () => ({}) },
    },
    data() {
        const store = useRosterStore();
        return {
            abilities: store.abilities,
            archetypes: store.archetypes,
            abilityTree: {},
            selectedAbilityList: this.initial.archetype || "",
            character: { abilities: [], stats: {}, ...this.initial },
            selectedSkill: null,
            selectedState: null,
        };
    },
    computed: {
        paths() {
            if (!this.selectedAbilityList || !this.abilityTree[this.selectedAbilityList]) {
                return [];
            }
            const archetypeDef = this.archetypes.find(
                (a) => a.archetype === this.selectedAbilityList
            );
            if (!archetypeDef || !Array.isArray(archetypeDef.paths)) {
                return Object.keys(this.abilityTree[this.selectedAbilityList])
                    .filter((p) => p !== "None" && p !== "All")
                    .sort();
            }
            const treePaths = Object.keys(this.abilityTree[this.selectedAbilityList]);
            return archetypeDef.paths
                .map((p) => p.name.replace(/^The\s+/i, "").trim())
                .filter((p) => treePaths.includes(p));
        },
        tiers() {
            if (!this.selectedAbilityList) return [];
            const paths = this.abilityTree[this.selectedAbilityList] || {};
            const allTiers = new Set();
            Object.values(paths).forEach((pathObj) => {
                Object.keys(pathObj).forEach((tier) => allTiers.add(Number(tier)));
            });
            if (paths["None"]?.[1]) allTiers.add(1);
            if (paths["All"]) {
                Object.keys(paths["All"]).forEach((tier) =>
                    allTiers.add(Number(tier))
                );
            }
            return Array.from(allTiers).sort((a, b) => a - b);
        },
    },
    watch: {
        initial: {
            deep: true,
            handler(newVal) {
                this.selectedAbilityList = newVal.archetype || "";
                this.character = { abilities: [], stats: {}, ...newVal };
                const modalEl = this.$refs.modalRef;
                const modalInstance = bootstrap.Modal.getInstance(modalEl);
                if (modalInstance) modalInstance.hide();
                this.selectedSkill = null;
                this.selectedState = null;
            },
        },
        selectedAbilityList(newVal) {
            this.$emit("updateSelectedAbilityList", newVal);
        },
    },
    created() {
        this.abilityTree = this.abilities.reduce((acc, ability) => {
            const { archetype, path, tier } = ability;
            if (!acc[archetype]) acc[archetype] = {};
            if (!acc[archetype][path]) acc[archetype][path] = {};
            if (!acc[archetype][path][tier]) acc[archetype][path][tier] = [];
            acc[archetype][path][tier].push(ability);
            return acc;
        }, {});
    },
    mounted() {
        const el = this.$refs.scrollRef;
        if (!el) return;
        let isDown = false;
        let startX;
        let scrollLeft;
        el.addEventListener("mousedown", (e) => {
            isDown = true;
            startX = e.pageX - el.offsetLeft;
            scrollLeft = el.scrollLeft;
        });
        el.addEventListener("mouseleave", () => {
            isDown = false;
        });
        el.addEventListener("mouseup", () => {
            isDown = false;
        });
        el.addEventListener("mousemove", (e) => {
            if (!isDown) return;
            e.preventDefault();
            const x = e.pageX - el.offsetLeft;
            const walk = (x - startX) * 1;
            el.scrollLeft = scrollLeft - walk;
        });
    },
    methods: {
        getNodeState(skill) {
            const statValue =
                skill.requiredStat &&
                skill.requiredStat.toLowerCase() !== "none"
                    ? this.character.stats?.[skill.requiredStat.toLowerCase()]?.skill || 0
                    : null;
            const meetsStat = statValue === null || statValue >= 5;
            const requiredAbility = skill.requiredAbility || [];
            const noRealRequirement =
                !requiredAbility.length ||
                requiredAbility.every((id) => id == null);
            const hasAbility =
                noRealRequirement ||
                requiredAbility.some((id) =>
                    this.character.abilities?.some((a) => a.id === id)
                );
            const hasAllRounder = this.character.background?.ability === "All Rounder";
            return this.character.abilities?.some((a) => a.id === skill.id)
                ? "Active"
                : hasAbility && (meetsStat || hasAllRounder)
                ? "available"
                : "unavailable";
        },
        openSkillModal(skill, state) {
            this.selectedSkill = skill;
            this.selectedState = state;
            const modalEl = this.$refs.modalRef;
            if (!document.body.contains(modalEl))
                document.body.appendChild(modalEl);
            const modal = new bootstrap.Modal(modalEl, {
                backdrop: "static",
                keyboard: true,
            });
            modal.show();
        },
        unlockAbility(skill) {
            const snapshot = JSON.parse(JSON.stringify(skill));
            this.$emit("unlockAbility", snapshot);
        },
        forgetAbility(skill) {
            this.$emit("forgetAbility", skill);
        },
    },
};
</script>

<style scoped>
/* Scroll wrapper */
.skill-tree-scroll {
    overflow-x: auto;
    overflow-y: hidden;
    width: 100%;
    cursor: grab;
}
.skill-tree-scroll:active {
    cursor: grabbing;
}

/* Main grid */
.skilltree {
    display: grid;
    gap: 1.5rem;
    margin: 1rem auto;
    width: 95%;
    max-width: 95%;
}

/* Each grid cell */
.tier-cell {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 1rem;
}

/* Default node behavior */
.skill-node {
    flex: 0 0 auto;
    text-align: center;
}

/* If multiple nodes in the same path+tier → split evenly */
.skill-node.split {
    flex: 1 1 0;
}

/* Special case: All/None nodes take the full row */
.full-width {
    grid-column: 1 / -1;
    justify-content: center;
}

/* Modal fade animation */
.modal.fade .modal-dialog {
    transform: scale(0.95);
    transition: transform 0.2s ease-out;
}
.modal.fade.show .modal-dialog {
    transform: scale(1);
}
</style>

